"use client";

import { ReportCitationMarker } from "./ReportCitationMarker";
import type { ReportExecSummaryBullet } from "./report-types";

export interface ReportExecSummaryProps {
  bullets: ReportExecSummaryBullet[];
  onOpenCitation?: (n: number) => void;
  /** Opens the existing formula trace for a resolved value reference. */
  onOpenTrace?: (nodeId: string) => void;
}

/** Executive-summary prose. Resolution and copy assembly happen upstream. */
export function ReportExecSummary({
  bullets,
  onOpenCitation,
  onOpenTrace,
}: ReportExecSummaryProps) {
  if (bullets.length === 0) {
    return (
      <p className="m-0 text-[12.5px] leading-[1.55] text-(--fg-muted)">
        No executive-summary findings are available yet.
      </p>
    );
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-[18px] p-0">
      {bullets.map((bullet) => (
        <li
          key={bullet.id}
          className="flex items-start text-[13.5px] leading-[1.55] text-(--fg-secondary)"
        >
          <span>
            <b className="font-bold text-(--fg-primary)">{bullet.label}</b>{" "}
            {bullet.body.map((segment, index) => {
              if (segment.kind === "text")
                return <span key={index}>{segment.text}</span>;
              if (!segment.traceNodeId || !onOpenTrace) {
                return <span key={index}>{segment.text}</span>;
              }
              return (
                <button
                  key={index}
                  type="button"
                  className="cursor-pointer border-0 bg-transparent p-0 font-inherit text-inherit underline decoration-(--border-strong) underline-offset-2"
                  aria-label={`View calculation for ${segment.text}`}
                  onClick={() => onOpenTrace(segment.traceNodeId ?? "")}
                >
                  {segment.text}
                </button>
              );
            })}
            {bullet.citation != null && (
              <ReportCitationMarker
                n={bullet.citation}
                onClick={onOpenCitation}
              />
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}
